import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, User, ChevronDown, Menu, Settings } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

/**
 * Modern Header Component
 * Clean navbar with logo and right-aligned profile
 */
export default function Header({ onMenuToggle, onQuickAction }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const handleSignOut = async () => {
    try {
      await signOut();
      setShowUserMenu(false);
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/60 shadow-soft">
      <div className="flex items-center justify-between h-16 px-4 lg:px-6">
        {/* Left: Logo and menu toggle */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 hover:bg-slate-100 rounded-xl transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="w-6 h-6 text-slate-600" />
          </button>

          <div className="hidden lg:flex items-center gap-3">
            <img 
              src="/logo.svg" 
              alt="Dalverha Logo" 
              className="w-10 h-10 object-contain"
              onError={(e) => {
                // Fallback to gradient if image not found
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'flex';
              }}
            />
            <div 
              className="w-10 h-10 bg-gradient-to-br from-primary-600 to-primary-500 rounded-xl hidden items-center justify-center shadow-soft"
            >
              <span className="text-white font-bold text-lg">D</span>
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Dalverha</h1>
              <p className="text-xs text-slate-500">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Right: Notifications and profile */}
        <div className="flex items-center gap-2">
          {/* Notifications */}
          <div className="relative">
            {/* <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative p-2.5 hover:bg-slate-100 rounded-xl transition-colors"
              aria-label="Notifications"
            >
              <Bell className="w-5 h-5 text-slate-600" />
              <span className="absolute top-2 right-2 w-2 h-2 bg-danger-500 rounded-full ring-2 ring-white animate-pulse" />
            </button> */}

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-soft-2xl border border-slate-200 z-50 animate-slide-down">
                <div className="p-4 border-b border-slate-100">
                  <h3 className="text-sm font-semibold text-slate-900">Notifications</h3>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="p-4 border-b border-slate-50 hover:bg-gradient-to-r hover:from-primary-50/30 hover:to-transparent cursor-pointer transition-colors">
                      <p className="text-sm font-medium text-slate-900">New delete request</p>
                      <p className="text-xs text-slate-500 mt-1">User #12345 requested account deletion</p>
                      <p className="text-xs text-slate-400 mt-1">5 minutes ago</p>
                    </div>
                  ))}
                </div>
                <div className="p-3 text-center border-t border-slate-100">
                  <button className="text-xs text-primary-600 hover:text-primary-700 font-medium">
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-3 p-2 pr-3 hover:bg-slate-100 rounded-xl transition-colors"
              aria-label="User menu"
            >
              <div className="w-9 h-9 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-soft">
                <User className="w-5 h-5 text-white" />
              </div>
              <div className="hidden lg:block text-left">
                <p className="text-sm font-medium text-slate-900">{user?.name || 'Admin'}</p>
                <p className="text-xs text-slate-500">{user?.type === 'admin_master' ? 'Master Admin' : user?.type === 'operator' ? 'Operator' : 'Admin'}</p>
              </div>
              <ChevronDown className="hidden lg:block w-4 h-4 text-slate-400" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-soft-2xl border border-slate-200 z-50 animate-slide-down">
                <div className="p-4 border-b border-slate-100">
                  <p className="text-sm font-semibold text-slate-900">{user?.name || 'Master Admin'}</p>
                  <p className="text-xs text-slate-500">{user?.email || 'admin@dalverha.com'}</p>
                </div>
                <div className="p-2">
                  <button 
                    onClick={() => {
                      navigate('/profile');
                      setShowUserMenu(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 rounded-xl transition-colors"
                  >
                    <Settings className="w-4 h-4" />
                    Profile Settings
                  </button>
                  <button
                    onClick={handleSignOut}
                    className="w-full text-left px-4 py-2.5 text-sm text-danger-600 hover:bg-danger-50 rounded-xl transition-colors"
                  >
                    Sign Out
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
